import { Injectable, inject } from '@angular/core';
import introJs from 'intro.js';
import { Subject } from 'rxjs';
import { LoggerService } from './logger.service';

export interface WalkthroughStep {
  element?: string | HTMLElement;
  intro: string;
  title?: string;
  position?: 'top' | 'left' | 'right' | 'bottom' | 'auto';
  tooltipClass?: string;
  highlightClass?: string;
  scrollTo?: 'tooltip' | 'element' | 'off';
}

export interface WalkthroughOptions {
  steps: WalkthroughStep[];
  showProgress?: boolean;
  showBullets?: boolean;
  exitOnOverlayClick?: boolean;
  exitOnEsc?: boolean;
  nextLabel?: string;
  prevLabel?: string;
  skipLabel?: string;
  doneLabel?: string;
  overlayOpacity?: number;
  scrollToElement?: boolean;
  scrollPadding?: number;
  disableInteraction?: boolean;
  helperElementPadding?: number;
}

@Injectable({
  providedIn: 'root'
})
export class WalkthroughService {
  private logger = inject(LoggerService);
  private introInstance: ReturnType<typeof introJs> | null = null;
  private readonly STORAGE_KEY_PREFIX = 'gemfin_walkthrough_';

  /** Emit on this to ask the active page component to restart its tour */
  // Kept as Subject: void event must fire on every call; signals dedupe and replay.
  private restartTourSubject = new Subject<void>();
  readonly restartTour$ = this.restartTourSubject.asObservable();

  /** Call from navbar Help button to trigger tour restart on the current page */
  triggerRestart(): void {
    this.restartTourSubject.next();
  }

  constructor() {
    this.logger.info(
      'WalkthroughService initialized',
      'WalkthroughService.constructor'
    );
  }

  /**
   * Check if a specific walkthrough has been completed
   */
  hasCompletedWalkthrough(walkthroughId: string): boolean {
    const key = `${this.STORAGE_KEY_PREFIX}${walkthroughId}`;
    return localStorage.getItem(key) === 'completed';
  }

  /**
   * Mark a walkthrough as completed
   */
  private markWalkthroughCompleted(walkthroughId: string): void {
    const key = `${this.STORAGE_KEY_PREFIX}${walkthroughId}`;
    localStorage.setItem(key, 'completed');
    this.logger.info(
      `Walkthrough '${walkthroughId}' marked as completed`,
      'WalkthroughService.markWalkthroughCompleted'
    );
  }

  /**
   * Reset a specific walkthrough (for testing or restart)
   */
  resetWalkthrough(walkthroughId: string): void {
    const key = `${this.STORAGE_KEY_PREFIX}${walkthroughId}`;
    localStorage.removeItem(key);
    this.logger.info(
      `Walkthrough '${walkthroughId}' reset`,
      'WalkthroughService.resetWalkthrough'
    );
  }

  /**
   * Start a guided walkthrough
   */
  startWalkthrough(
    walkthroughId: string,
    options: WalkthroughOptions
  ): ReturnType<typeof introJs> {
    // Clean up any existing instance
    if (this.introInstance) {
      try {
        this.introInstance.exit(true);
      } catch {
        // Ignore errors during cleanup
      }
    }

    this.logger.info(
      `Starting walkthrough: ${walkthroughId}`,
      'WalkthroughService.startWalkthrough'
    );

    // Initialize Intro.js
    this.introInstance = introJs();

    // Prepare steps with progress indicators
    const stepsWithProgress = options.steps.map((step, index) => {
      const totalSteps = options.steps.length;
      const progressText =
        options.showProgress !== false
          ? `<div class="intro-progress">Step ${index + 1} of ${totalSteps}</div>`
          : '';

      return {
        element: step.element,
        intro: `${progressText}<div class="intro-title">${step.title || ''}</div><div class="intro-description">${step.intro}</div>`,
        position: (step.position ?? 'auto') as
          'top' | 'left' | 'right' | 'bottom',
        tooltipClass: step.tooltipClass || 'introjs-custom-tooltip',
        highlightClass: step.highlightClass || '',
        scrollTo: step.scrollTo || 'tooltip'
      };
    });

    // Configure Intro.js
    this.introInstance.setOptions({
      steps: stepsWithProgress,
      showProgress: false, // We handle progress manually
      showBullets: options.showBullets ?? true,
      exitOnOverlayClick: options.exitOnOverlayClick ?? false,
      exitOnEsc: options.exitOnEsc ?? true,
      nextLabel: options.nextLabel || 'Next',
      prevLabel: options.prevLabel || 'Back',
      skipLabel: options.skipLabel || 'Skip Tour',
      doneLabel: options.doneLabel || 'Got it!',
      overlayOpacity: options.overlayOpacity ?? 0.7,
      scrollToElement: options.scrollToElement ?? true,
      scrollPadding: options.scrollPadding ?? 30,
      disableInteraction: options.disableInteraction ?? true,
      helperElementPadding: options.helperElementPadding ?? 10,
      tooltipPosition: 'auto',
      positionPrecedence: ['bottom', 'top', 'right', 'left']
    });

    // Event handlers
    this.introInstance.oncomplete(() => {
      this.markWalkthroughCompleted(walkthroughId);
      this.logger.userAction(
        'Walkthrough completed',
        { walkthroughId },
        'WalkthroughService.oncomplete'
      );
    });

    this.introInstance.onexit(() => {
      this.logger.userAction(
        'Walkthrough exited',
        { walkthroughId },
        'WalkthroughService.onexit'
      );
    });

    this.introInstance.onafterchange((targetElement: HTMLElement) => {
      // Add smooth scroll behavior with horizontal scrolling support,
      // then refresh the tooltip position so it aligns with the newly
      // scrolled element (fixes off-screen elements in scrollable tables).
      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
          // Give the scroll animation time to settle, then re-position the tooltip
          setTimeout(() => {
            try {
              this.introInstance?.refresh();
            } catch {
              // ignore refresh errors
            }
          }, 200);
        }, 100);
      }

      // Move skip button to footer and position on left
      const currentStep = this.introInstance?.currentStep() ?? 0;
      const totalSteps = options.steps.length;
      const skipButton = document.querySelector(
        '.introjs-skipbutton'
      ) as HTMLElement;
      const buttonContainer = document.querySelector(
        '.introjs-tooltipbuttons'
      ) as HTMLElement;

      if (skipButton && buttonContainer) {
        // Move skip button into button container if not already there
        if (skipButton.parentElement !== buttonContainer) {
          buttonContainer.insertBefore(skipButton, buttonContainer.firstChild);
        }

        // Hide skip button on last step
        if (currentStep === totalSteps - 1) {
          skipButton.style.display = 'none';
        } else {
          skipButton.style.display = '';
        }
      }

      // Toggle toolbar highlight when the toolbar step is active
      try {
        // Remove any existing highlights
        document.querySelectorAll('.tour-highlight').forEach((el) => {
          el.classList.remove('tour-highlight');
        });

        const stepIndex = currentStep;
        const stepDef = options.steps && options.steps[stepIndex];
        if (stepDef && stepDef.element === '#tour-toolbar') {
          const toolbar = document.querySelector('#tour-toolbar');
          if (toolbar) {
            // Find candidate icon elements immediately before the Add Client button.
            // Strategy: select buttons/icons inside the toolbar that contain an <i> or <img> and are not the Add Client button.
            const candidates: Element[] = [];

            // common patterns: icon buttons with <i> (PrimeIcons), buttons with img (excel), or plain <button>
            const allButtons = Array.from(
              toolbar.querySelectorAll('button, a, div')
            );
            for (const el of allButtons) {
              // skip if element contains visible text (likely Add Client)
              const text = (el.textContent || '').trim();
              const hasIcon = !!el.querySelector('i, img, svg');
              if (hasIcon && text.length === 0) {
                candidates.push(el);
              }
            }

            // If we still have more than desired, take the right-most 3 candidates
            let toHighlight = candidates;
            if (candidates.length > 3) {
              toHighlight = candidates.slice(-3);
            }

            toHighlight.forEach((el) => {
              // add class to the element itself
              try {
                el.classList.add('tour-highlight');
              } catch {
                // ignore classList errors
              }
              // also add to the inner icon if present for stronger styling
              const inner = el.querySelector('i, img, svg');
              if (inner) {
                try {
                  inner.classList.add('tour-highlight');
                } catch {
                  // ignore classList errors
                }
              }
            });
          }
        }
      } catch {
        // ignore highlight failures
      }
    });

    // Start the tour
    this.introInstance.start();

    // Move skip button to footer immediately after start
    setTimeout(() => {
      const skipButton = document.querySelector(
        '.introjs-skipbutton'
      ) as HTMLElement;
      const buttonContainer = document.querySelector(
        '.introjs-tooltipbuttons'
      ) as HTMLElement;

      if (
        skipButton &&
        buttonContainer &&
        skipButton.parentElement !== buttonContainer
      ) {
        buttonContainer.insertBefore(skipButton, buttonContainer.firstChild);
      }
    }, 50);

    return this.introInstance;
  }

  /**
   * Auto-start walkthrough only on first visit
   */
  autoStartWalkthrough(
    walkthroughId: string,
    options: WalkthroughOptions,
    delayMs: number = 500
  ): void {
    if (!this.hasCompletedWalkthrough(walkthroughId)) {
      setTimeout(() => {
        this.startWalkthrough(walkthroughId, options);
      }, delayMs);
    } else {
      this.logger.info(
        `Walkthrough '${walkthroughId}' already completed, skipping auto-start`,
        'WalkthroughService.autoStartWalkthrough'
      );
    }
  }

  /**
   * Manually restart a walkthrough (ignores completion status)
   */
  restartWalkthrough(walkthroughId: string, options: WalkthroughOptions): void {
    this.logger.userAction(
      'Restarting walkthrough',
      { walkthroughId },
      'WalkthroughService.restartWalkthrough'
    );
    this.startWalkthrough(walkthroughId, options);
  }

  /**
   * Exit the current walkthrough
   */
  exitWalkthrough(): void {
    if (this.introInstance) {
      try {
        this.introInstance.exit(true);
      } catch {
        // Ignore errors during exit
      }
      this.introInstance = null;
    }
    // Force-remove any remaining intro.js DOM elements from <body>
    this.cleanupIntroJsElements();
    // Schedule additional cleanup passes to catch any pending setTimeout
    // callbacks from intro.js onafterchange handler (which uses 100ms + 200ms delays)
    setTimeout(() => this.cleanupIntroJsElements(), 150);
    setTimeout(() => this.cleanupIntroJsElements(), 500);
  }

  /**
   * Forcibly remove all intro.js DOM artifacts from the page.
   * intro.js appends overlays and tooltips directly to <body>, so they
   * can survive Angular route changes if exit() doesn't fully clean up.
   */
  private cleanupIntroJsElements(): void {
    try {
      document
        .querySelectorAll(
          '.introjs-overlay, .introjs-helperLayer, ' +
            '.introjs-tooltipReferenceLayer, .introjs-disableInteraction, ' +
            '.introjs-helperNumberLayer, .introjs-fixedTooltip, ' +
            '.introjs-tooltip, .introjs-bullets, .introjs-progress, ' +
            '.introjs-tooltipbuttons, .introjs-arrow'
        )
        .forEach((el) => el.remove());

      document
        .querySelectorAll('.introjs-showElement')
        .forEach((el) =>
          el.classList.remove('introjs-showElement', 'introjs-relativePosition')
        );

      document
        .querySelectorAll('.tour-highlight')
        .forEach((el) => el.classList.remove('tour-highlight'));

      // Remove introjs-fixParent class from body if present
      document.body.classList.remove('introjs-fixParent');
    } catch {
      // Ignore cleanup errors
    }
  }

  /**
   * Go to a specific step
   */
  goToStep(stepNumber: number): void {
    if (this.introInstance) {
      this.introInstance.goToStep(stepNumber);
    }
  }

  /**
   * Get current step index
   */
  getCurrentStep(): number {
    return this.introInstance ? this.introInstance.currentStep() : -1;
  }
}
